import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { savePhotoMetadata, uploadPhotoToR2 } from './usePhotoUpload'
import {
  Button,
  Card,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type Photo = {
  id: string
  filename?: string
  caption?: string | null
  photo_url?: string | null
  view_url?: string | null
  thumbnail_url?: string | null
  thumbnail_view_url?: string | null
  created_at?: number | null
}

type PhotoListResponse = {
  photos: Photo[]
  total?: number
}

export function PhotoGalleryPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()

  const list = useQuery<PhotoListResponse>({
    queryKey: ['photos', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<PhotoListResponse>(orgPath(`/photos?project_id=${projectId}`)),
  })

  const photos = list.data?.photos ?? []

  const [file, setFile] = useState<File | null>(null)
  const [caption, setCaption] = useState('')

  const upload = useMutation({
    mutationFn: async () => {
      if (!file || !projectId) throw new Error('Pick a file first.')
      const uploaded = await uploadPhotoToR2(file, { projectId })
      return savePhotoMetadata({
        photo: uploaded,
        projectId,
        caption: caption || undefined,
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => {
      setFile(null)
      setCaption('')
      qc.invalidateQueries({ queryKey: ['photos', projectId] })
    },
  })

  if (list.isLoading) return <Loading />
  if (list.isError) return <ErrorBanner error={list.error} />

  return (
    <div className="space-y-6">
      <PageHeader title="Photos" subtitle="Site photos uploaded against this project." />

      <Card>
        <h2 className="font-semibold mb-3">Upload</h2>
        <form
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          onSubmit={(e) => {
            e.preventDefault()
            upload.mutate()
          }}
        >
          <Field label="File (jpeg/png/webp/heic, ≤10MB)">
            <Input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <Field label="Caption (optional)">
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} />
          </Field>
          {upload.isError && (
            <div className="sm:col-span-2">
              <ErrorBanner error={upload.error} />
            </div>
          )}
          <div className="sm:col-span-2 flex justify-end">
            <Button type="submit" disabled={!file || upload.isPending}>
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </form>
      </Card>

      {photos.length === 0 ? (
        <Empty message="No photos yet." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p) => (
            <Card key={p.id} className="p-2">
              <div className="aspect-square overflow-hidden rounded-md bg-surface-950 flex items-center justify-center">
                {p.thumbnail_view_url || p.view_url || p.thumbnail_url || p.photo_url ? (
                  <img
                    src={
                      p.thumbnail_view_url ??
                      p.view_url ??
                      p.thumbnail_url ??
                      p.photo_url ??
                      ''
                    }
                    alt={p.caption ?? p.filename ?? 'photo'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-xs text-surface-400">No preview</span>
                )}
              </div>
              <div className="px-1 pt-2 text-xs">
                {p.caption && <div className="text-surface-100">{p.caption}</div>}
                <div className="text-surface-400">
                  <FormattedDate ts={p.created_at ?? null} />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
